import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ClearIcon from '@mui/icons-material/Clear'
import { Box, FormControl, IconButton, Typography, useTheme } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

const renderContextLabel = (context: string) => {
  switch (context) {
    case 'startDate':
      return 'Start Date'
    case 'endDate':
      return 'End Date'
    default:
      return context
  }
}

interface CustomDateRangeFilterProps {
  context: string
  filterModel: any
  onSetDateFilter: (fieldName: string, fromDate: string | null, toDate: string | null) => void
}

const CustomDateRangeFilter = ({ context, filterModel, onSetDateFilter }: CustomDateRangeFilterProps) => {
  const theme = useTheme()
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)

  // Sync state with filterModel
  useEffect(() => {
    const fromFilter = filterModel?.items?.find(
      (item: any) => item.field === context && item.operator === 'onOrAfter'
    )
    const toFilter = filterModel?.items?.find((item: any) => item.field === context && item.operator === 'onOrBefore')

    setFromDate(fromFilter?.value ? dayjs(fromFilter.value) : null)
    setToDate(toFilter?.value ? dayjs(toFilter.value) : null)
  }, [filterModel, context])

  const handleFromDateChange = (newValue: Dayjs | null) => {
    setFromDate(newValue)
    onSetDateFilter(context, newValue ? newValue.format('YYYY-MM-DD') : null, toDate ? toDate.format('YYYY-MM-DD') : null)
  }

  const handleToDateChange = (newValue: Dayjs | null) => {
    setToDate(newValue)
    onSetDateFilter(
      context,
      fromDate ? fromDate.format('YYYY-MM-DD') : null,
      newValue ? newValue.format('YYYY-MM-DD') : null
    )
  }

  const handleClear = () => {
    setFromDate(null)
    setToDate(null)
    onSetDateFilter(context, null, null)
  }

  const hasValue = fromDate || toDate

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 220,
          gap: 1,
          p: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
              {renderContextLabel(context)}
            </Typography>
          </Box>
          {hasValue && (
            <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl sx={{ flex: 1 }} size="small">
            <DatePicker
              label="From"
              value={fromDate}
              onChange={handleFromDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-root': {
                      height: 32,
                      fontSize: 12,
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: 12,
                    },
                  },
                },
              }}
            />
          </FormControl>
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>–</Typography>
          <FormControl sx={{ flex: 1 }} size="small">
            <DatePicker
              label="To"
              value={toDate}
              onChange={handleToDateChange}
              minDate={fromDate || undefined}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-root': {
                      height: 32,
                      fontSize: 12,
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: 12,
                    },
                  },
                },
              }}
            />
          </FormControl>
        </Box>
      </Box>
    </LocalizationProvider>
  )
}

export default CustomDateRangeFilter
